import React from 'react';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Save as SaveIcon,
  OpenInNew as PopOutIcon,
  Close as CloseIcon,
  BugReport as DebugIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  Lock as LockIcon,
  InfoOutlined as PropertiesIcon,
} from '@mui/icons-material';

/**
 * FlexToolbar - Top toolbar for FLEX Studio
 *
 * Features:
 * - Script name display
 * - Save, Run buttons
 * - Pop-out window button
 * - Debug/trace toggle
 */
function FlexToolbar({
  scriptName,
  isModified = false,
  isNewScript = false,
  isRunning = false,
  isSaving = false,
  traceEnabled = false,
  canEdit = true,
  isLocked = false,
  onSave,
  onRun,
  onToggleTrace,
  onRefresh,
  onPopOut,
  onClose,
  onOpenProperties,
  isPopout = false,
}) {
  // Allow save if: (new script OR modified) AND user can edit
  const canSave = (isNewScript || isModified) && canEdit;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 48,
      }}
    >
      {/* Script name and status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
        <CodeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          {scriptName || 'Untitled'}
        </Typography>
        {isNewScript && (
          <Chip
            label="New"
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              bgcolor: 'info.main',
              color: 'info.contrastText',
            }}
          />
        )}
        {isModified && !isNewScript && (
          <Chip
            label="Modified"
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
            }}
          />
        )}
        {isLocked && (
          <Tooltip title={canEdit ? "Script is locked (you can edit as creator)" : "Script is locked (view only)"}>
            <Chip
              icon={<LockIcon sx={{ fontSize: 14 }} />}
              label={canEdit ? "Locked (Owner)" : "Locked"}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                bgcolor: canEdit ? 'info.main' : 'error.main',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Properties panel (version/domain/category/extends + input/output schema) */}
        {onOpenProperties && (
          <Tooltip title="Script properties (version, domain, extends, schema)">
            <IconButton size="small" onClick={onOpenProperties} sx={{ color: 'text.secondary' }}>
              <PropertiesIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Trace toggle */}
        <Tooltip title={traceEnabled ? 'Disable trace mode' : 'Enable trace mode'}>
          <IconButton
            size="small"
            onClick={onToggleTrace}
            sx={{
              color: traceEnabled ? 'primary.main' : 'text.secondary',
              bgcolor: traceEnabled ? 'action.selected' : 'transparent',
            }}
          >
            <DebugIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Save button */}
        <Tooltip title={!canEdit && isLocked ? "Script is locked - view only" : "Save (Ctrl+S)"}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={!canSave || isSaving}
              sx={{ minWidth: 80 }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </span>
        </Tooltip>

        {/* Run button */}
        <Tooltip title="Run (F5)">
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<RunIcon />}
              onClick={onRun}
              disabled={isRunning}
              sx={{ minWidth: 80 }}
            >
              {isRunning ? 'Running...' : 'Run'}
            </Button>
          </span>
        </Tooltip>

        {onRefresh && (
          <Tooltip title="Reload scripts from database">
            <IconButton size="small" onClick={onRefresh} sx={{ color: 'text.secondary' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Pop-out / Close button */}
        {isPopout ? (
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Open in new window">
            <IconButton size="small" onClick={onPopOut}>
              <PopOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default FlexToolbar;
